import { useEffect, useMemo, useState } from "react";
import { copy } from "../copy";
import { formatBps } from "../format";
import type { Market, MarketOutcome } from "./api";
import { Avatar } from "./ui";

const STEP_MS = 650;

// Last place first, the winner last. Share order is display only; the winner
// is whoever the stored settlement names.
export function revealOrder(market: Market): MarketOutcome[] {
  const winnerId = market.settlement?.winner_producer_id;
  const rest = market.outcomes.filter((o) => o.producer_id !== winnerId).sort((a, b) => a.share_bps - b.share_bps);
  const winner = market.outcomes.find((o) => o.producer_id === winnerId);
  return winner ? [...rest, winner] : rest;
}

function PayoutPanel({ market }: { market: Market }) {
  const r = copy.contest.reveal;
  const { payout, picks } = market.viewer;
  if (payout) {
    return (
      <section data-testid="payout-panel" className="bf-rise mt-6 rounded-2xl border border-money/40 bg-money/5 p-5">
        <p className="text-[10px] uppercase tracking-[0.2em] text-chrome-dim">{r.payoutHeading}</p>
        <p className="mt-1 font-headline text-2xl uppercase text-chrome">{r.won}</p>
        <p data-testid="payout-amount" className="bf-tabular mt-2 font-headline text-5xl leading-none text-money">
          {payout.amount_display}
        </p>
        <p data-testid="payout-status" className="mt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-chrome-dim">
          {payout.paid ? r.paid : r.pending}
        </p>
      </section>
    );
  }
  if (picks.length === 0) return null;
  return (
    <section data-testid="payout-panel" className="bf-rise mt-6 rounded-2xl border border-ground-edge bg-ground-raised p-5">
      <p className="text-[10px] uppercase tracking-[0.2em] text-chrome-dim">{r.payoutHeading}</p>
      <p className="mt-1 font-headline text-2xl uppercase text-chrome">{r.lost}</p>
      <p className="mt-2 text-[12px] leading-relaxed text-chrome-dim">{r.lostSub}</p>
    </section>
  );
}

// The settled Vote tab. Presentation only: every share, odds string, and
// amount is the stored settlement as the market read returns it.
export default function Reveal({ market, reducedMotion }: { market: Market; reducedMotion: boolean }) {
  const r = copy.contest.reveal;
  const order = useMemo(() => revealOrder(market), [market]);
  const winnerId = market.settlement?.winner_producer_id;
  // Reduced motion shows everything from the first paint.
  const [shown, setShown] = useState(reducedMotion ? order.length : 0);

  useEffect(() => {
    if (reducedMotion) {
      setShown(order.length);
      return;
    }
    if (shown >= order.length) return;
    const t = window.setTimeout(() => setShown((n) => n + 1), STEP_MS);
    return () => window.clearTimeout(t);
  }, [reducedMotion, shown, order.length]);

  const done = shown >= order.length;
  const s = market.settlement;

  return (
    <div data-testid="reveal">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h3 className="font-headline text-2xl uppercase leading-none text-chrome">{r.heading}</h3>
        {s && (
          <p data-testid="reveal-split" className="text-[11px] text-chrome-dim">
            {r.split(s.pool_display, s.rake_display, s.producer_cut_display)}
          </p>
        )}
      </div>

      {reducedMotion ? (
        <table data-testid="reveal-table" className="mt-5 w-full table-fixed border-collapse text-left">
          <caption className="sr-only">{r.table}</caption>
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">
              <th className="pb-2 font-normal">{r.producer}</th>
              <th className="w-20 pb-2 text-right font-normal">{r.share}</th>
              <th className="w-24 pb-2 text-right font-normal">{r.odds}</th>
            </tr>
          </thead>
          <tbody>
            {[...order].reverse().map((o) => (
              <tr key={o.producer_id} data-testid="reveal-row" className="border-t border-ground-edge">
                <td className="truncate py-3 text-sm font-bold text-chrome">
                  @{o.username}
                  {o.producer_id === winnerId && <span className="ml-2 text-[10px] uppercase tracking-[0.16em] text-gold">{r.winner}</span>}
                </td>
                <td className="bf-tabular py-3 text-right text-sm text-chrome">{formatBps(o.share_bps)}</td>
                <td className="bf-tabular py-3 text-right text-sm text-chrome-dim">{o.american_odds}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <ol data-testid="reveal-cards" className="mt-5 space-y-3">
          {order.slice(0, shown).map((o) => {
            const winner = o.producer_id === winnerId;
            return (
              <li key={o.producer_id} data-testid="reveal-card" className="bf-reveal-card">
                <div
                  className={`bf-reveal-in flex items-center gap-3 rounded-2xl border bg-ground-raised p-4 ${
                    winner ? "bf-winner-rise border-gold shadow-gold" : "border-ground-edge"
                  }`}
                >
                  <Avatar username={o.username} logoUrl={o.logo_url} size={winner ? "h-14 w-14" : "h-10 w-10"} />
                  <div className="min-w-0 flex-1">
                    {winner && <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold">{r.winner}</p>}
                    <p className={`truncate font-bold text-chrome ${winner ? "font-headline text-2xl uppercase" : "text-sm"}`}>
                      @{o.username}
                    </p>
                  </div>
                  <p className={`bf-tabular shrink-0 text-right ${winner ? "text-gold" : "text-chrome-dim"}`}>
                    <span className="block font-headline text-xl leading-none">{formatBps(o.share_bps)}</span>
                    <span className="block text-[10px] uppercase tracking-[0.14em]">{o.american_odds}</span>
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {done && <PayoutPanel market={market} />}
    </div>
  );
}
